import React, {useState, useEffect} from 'react';
import {useLocation, useHistory} from 'react-router-dom';
import Box from '@material-ui/core/Box';
import {useDebouncedCallback} from 'use-debounce';
import Table, {
    Sort,
    getInitialPage,
    getInitialSort,
    getInitialRowsPerPage,
} from '@pluto/table';
import useQuery from '@pluto/query-hook';
import {setQuery} from '@pluto/set-history-query';
import {handleSort, handleMount} from 'Users/UsersList/UserListHandlers';
import {adminRoutes} from 'App/AppRouter/routes';
import IngestConfigsToolbar from './IngestConfigsToolbar';
import ingestConfigsTableColumns from './IngestConfigsColumns';
import {
    handlePaging,
    handleDebounce,
    renderEditIcon,
    getIngestConfigsFilters,
    handleDebounceCallback,
} from './IngestConfigsHandlers';
import {IngestSelectList} from './IngestConfigTypes';
import {IngestDataPayload} from '../IngestData/IngestDataTypes';
import './IngestConfigs.scss';

export interface IngestConfigsProps {
    ingestConfigs: IngestDataPayload[];
    total: number;
    setIngestConfigs: Function;
    ingestTypes: IngestSelectList;
    ingestStatuses: IngestSelectList;
    labPartners: IngestSelectList;
}

export default function IngestConfigsContainer(props: IngestConfigsProps) {
    const {
        ingestConfigs,
        total,
        setIngestConfigs,
        ingestTypes,
        ingestStatuses,
        labPartners,
    } = props;
    const query = useQuery();
    const history = useHistory();
    const {search} = useLocation();
    const initialSort = getInitialSort(query);
    const [sort, setSort] = useState<Sort>(initialSort);
    const [name, setName] = useState('');
    const [filters, setFilter] = useState({
        contentPartnerId: query.get('contentPartnerId') || '',
        status: query.get('status') || '',
        ingestType: query.get('ingestType') || '',
        labPartner: query.get('labPartner') || '',
    });
    const initialRowsPerPage = getInitialRowsPerPage(query);
    const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
    const initialPage = getInitialPage({
        query,
        rowsPerPage,
    });
    const [page, setPage] = useState(initialPage);
    const route = adminRoutes.ingestConfigs;
    const DEBOUNCE_TIME = 700;

    const params = {
        ...filters,
        ...sort,
        name,
        offset: rowsPerPage * page,
        limit: rowsPerPage,
    };

    const filterHandlers = {
        setFilter,
        setPage,
        setQuery,
        history,
        getList: setIngestConfigs,
        filters,
        rowsPerPage,
        name,
        route,
    };

    const {
        handlePartnersChange,
        handleStatusChange,
        handleIngestTypeChange,
        handleLabPartnerChange,
    } = getIngestConfigsFilters(filterHandlers);

    const onSort = handleSort({
        setSort,
        getList: setIngestConfigs,
        params,
        setQuery,
        history,
        route,
    });

    const debounceCallback = handleDebounceCallback({
        ...params,
        offset: 0,
        getList: setIngestConfigs,
        setPage,
        setQuery,
        history,
        route,
    });

    const [debouncedFunction] = useDebouncedCallback(debounceCallback, DEBOUNCE_TIME);

    const onDebouncedSearch = handleDebounce({
        debouncedFunction,
        setName,
    });

    const onPaging = handlePaging({
        ...params,
        getList: setIngestConfigs,
        setQuery,
        history,
        route,
    });

    const onMount = handleMount({
        getList: setIngestConfigs,
        setRowsPerPage,
        setPage,
        params,
        query,
    });

    useEffect(onMount, []);

    const cols = [
        ...ingestConfigsTableColumns,
        {
            dataKey: '',
            title: '',
            render: renderEditIcon(search),
        },
    ];
    return (
        <Box className="ingest-configs">
            <IngestConfigsToolbar
                handleNameChange={onDebouncedSearch}
                handleStatusChange={handleStatusChange}
                handlePartnersChange={handlePartnersChange}
                handleIngestTypeChange={handleIngestTypeChange}
                handleLabPartnerChange={handleLabPartnerChange}
                name={name}
                filters={filters}
                types={ingestTypes}
                statuses={ingestStatuses}
                labPartners={labPartners}
            />
            <Table
                setSort={onSort}
                sort={sort}
                data={ingestConfigs}
                columns={cols}
                total={total}
                onChangePageHandler={onPaging}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
            />
        </Box>
    );
}
